import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import {
  GetAvailableCountriesDocument,
  type GetAvailableCountriesQuery,
} from '../../../../../../graphql/fragment/audiences/GetAvailableCountries.generated.tsx';
import type { ExportTypeValue } from '../../../constants.ts';
import { US_REGION_CODE } from '../../../constants.ts';
import { buildSegmentVisibilityWhere } from './utils/filterBuilder.ts';
import { getDisplayNameByCode } from './utils/mapCodesToNames.ts';

export type UseMarketCountriesProps = {
  selectedRegionCode: string | null;
  selectedCountries: string[];
  setSelectedCountries: (countries: string[] | null) => void;
  accountId?: string;
  isOaMatchType?: boolean;
  providerId?: string;
  exportType?: ExportTypeValue | null;
};

export type UseMarketCountriesReturn = {
  countriesLoading: boolean;
  countries: { code: string; name: string }[];
  handleAddCountry: (code: string) => void;
  handleRemoveCountry: (code: string) => void;
  getCountryName: (code: string) => string;
};

export const useMarketCountries = ({
  selectedRegionCode,
  selectedCountries,
  setSelectedCountries,
  accountId = '',
  isOaMatchType = false,
  providerId,
  exportType,
}: UseMarketCountriesProps): UseMarketCountriesReturn => {
  const shouldFetchCountries =
    Boolean(accountId) && selectedRegionCode != null && selectedRegionCode !== US_REGION_CODE;

  const segmentWhere = useMemo(
    () =>
      shouldFetchCountries
        ? buildSegmentVisibilityWhere({
            accountId,
            isOaMatchType,
            providerId,
            exportType,
          })
        : undefined,
    [shouldFetchCountries, accountId, isOaMatchType, providerId, exportType]
  );

  const { data: countriesData, loading: countriesLoading } = useQuery<GetAvailableCountriesQuery>(
    GetAvailableCountriesDocument,
    {
      skip: !shouldFetchCountries,
      variables: {
        filter: { used: true, region: selectedRegionCode, where: segmentWhere },
      },
    }
  );

  const countries = useMemo(
    () =>
      (countriesData?.optionsByPath ?? [])
        .filter((country): country is NonNullable<typeof country> => country !== null)
        .map(country => ({ code: country.id, name: country.name })),
    [countriesData?.optionsByPath]
  );

  const handleAddCountry = useCallback(
    (code: string) => {
      if (selectedCountries.includes(code)) return;

      setSelectedCountries([...selectedCountries, code]);
    },
    [selectedCountries, setSelectedCountries]
  );

  const handleRemoveCountry = useCallback(
    (code: string) => {
      setSelectedCountries(selectedCountries.filter(country => country !== code));
    },
    [selectedCountries, setSelectedCountries]
  );

  const getCountryName = useCallback((code: string) => getDisplayNameByCode(code, countries), [countries]);

  return {
    countriesLoading,
    countries,
    handleAddCountry,
    handleRemoveCountry,
    getCountryName,
  };
};
